import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface TicketTypeDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  ticketType: {
    id: string;
    name: string;
    events?: Array<any>;
  } | null;
  productionId: string;
  onSuccess: () => void;
}

const TicketTypeDeleteDialog: React.FC<TicketTypeDeleteDialogProps> = ({
  open,
  onClose,
  ticketType,
  productionId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!ticketType) return;

    setLoading(true);
    setError(null);

    try {
      await passkitAPI.deleteTicketType(productionId, ticketType.id);

      logger.log('✅ Ticket Type Deleted:', ticketType.id);

      onSuccess();
      handleClose();
    } catch (err: any) {
      logger.error('Error deleting ticket type:', err);
      setError(err.response?.data?.detail || 'Greška pri brisanju ticket type-a');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const eventsCount = ticketType?.events?.length || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Obriši Ticket Type
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Da li ste sigurni da želite da obrišete ticket type:
        </Typography>
        <Typography variant="h6" sx={{ my: 2 }}>
          {ticketType?.name}
        </Typography>

        {eventsCount > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Ovaj ticket type ima <strong>{eventsCount}</strong> {eventsCount === 1 ? 'događaj' : 'događaja'}.
            Brisanjem ticket type-a, svi povezani događaji će takođe biti obrisani.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Ova akcija se ne može poništiti.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Otkaži
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Obriši'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketTypeDeleteDialog;
